import { Text, Paper, Stack, Spoiler, Badge, Group } from "@mantine/core";
import { IconSearch, IconLink } from "@tabler/icons-react";
import Markdown from "../_Common/Markdown";
import CopyTextActionIcon from "../Playground/CopyTextActionIcon";
import JsonBlock from "../_Common/JsonBlock";
import CitationsList from "../_Common/CitationsList";

function GroundingMetadata({ groundingMetadata }) {
  if (!groundingMetadata) return null;

  const hasSearchQueries = groundingMetadata.webSearchQueries?.length > 0;
  const hasUrlContext = groundingMetadata.urlContext?.length > 0;

  if (!hasSearchQueries && !hasUrlContext) return null;

  return (
    <Spoiler maxHeight={0} showLabel="Show grounding details" hideLabel="Hide">
      <Stack gap="xs" mt="sm">
        {hasSearchQueries && (
          <Group gap="xs" wrap="wrap">
            <Badge
              size="xs"
              variant="light"
              color="orange"
              leftSection={<IconSearch size={10} />}
            >
              Search queries
            </Badge>
            {groundingMetadata.webSearchQueries.map((query, i) => (
              <Badge key={i} size="xs" variant="outline" color="gray">
                {query}
              </Badge>
            ))}
          </Group>
        )}
        {hasUrlContext && (
          <Group gap="xs" wrap="wrap">
            <Badge
              size="xs"
              variant="light"
              color="blue"
              leftSection={<IconLink size={10} />}
            >
              URL context
            </Badge>
            {groundingMetadata.urlContext.map((url, i) => (
              <Badge
                key={i}
                size="xs"
                variant="outline"
                color={url.status === "SUCCESS" ? "green" : "red"}
              >
                {url.url}
              </Badge>
            ))}
          </Group>
        )}
      </Stack>
    </Spoiler>
  );
}

export default function InferenceResponse({
  response,
  displayMode = "markdown",
}) {
  const hasToolCalls = response.toolCalls?.length > 0;
  const responseText = response.textResponse ?? response.verbatimResponse;

  if (
    !responseText &&
    !hasToolCalls &&
    !response.citations?.length &&
    !response.groundingMetadata
  )
    return null;

  return (
    <Paper radius="sm" mb="md" p="xs" withBorder>
      <Stack gap="sm">
        {responseText && (
          <div className="markdown" style={{ position: "relative" }}>
            <CopyTextActionIcon
              style={{ position: "absolute", top: 8, right: 8 }}
              text={responseText}
            />
            {displayMode == "markdown" && (
              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                <Markdown>{responseText}</Markdown>
              </div>
            )}
            {displayMode == "mono" && (
              <JsonBlock
                value={responseText}
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              />
            )}
            {displayMode == "raw" && (
              <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {responseText}
              </Text>
            )}
          </div>
        )}
        {hasToolCalls && !responseText && (
          <Text size="xs" c="dimmed" fs="italic">
            No text — responded with {response.toolCalls.length} tool{" "}
            {response.toolCalls.length === 1 ? "call" : "calls"}
          </Text>
        )}
        <CitationsList citations={response.citations} />
        <GroundingMetadata groundingMetadata={response.groundingMetadata} />
      </Stack>
    </Paper>
  );
}
